/**
 * Deterministic, ASCII-only slugs for report library entries.
 *
 * The reports table has no slug column, so the slug is derived from the
 * title (plus organization/year when two titles collide). The function is
 * pure and shared by the route, the library UI and the sitemap so all three
 * always agree on the URL.
 */
export function slugifyReport(input: string): string {
  return input
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/&/g, " ve ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 90)
    .replace(/-+$/g, "");
}

type SluggableReport = {
  id: string;
  title: string;
  organization?: string | null;
  year?: number | null;
};

/**
 * Map report id -> unique slug. Collisions are disambiguated with the
 * organization and then the year, keeping the result stable for a given list.
 */
export function reportSlugMap<T extends SluggableReport>(reports: T[]): Map<string, string> {
  const used = new Set<string>();
  const map = new Map<string, string>();
  for (const r of [...reports].sort((a, b) => a.id.localeCompare(b.id))) {
    let slug = slugifyReport(r.title) || slugifyReport(r.organization ?? "") || r.id.slice(0, 8);
    if (used.has(slug) && r.organization) slug = `${slug}-${slugifyReport(r.organization)}`;
    if (used.has(slug) && r.year) slug = `${slug}-${r.year}`;
    let n = 2;
    while (used.has(slug)) slug = `${slug}-${n++}`;
    used.add(slug);
    map.set(r.id, slug);
  }
  return map;
}

export function findReportBySlug<T extends SluggableReport>(
  reports: T[],
  slug: string,
): T | undefined {
  const map = reportSlugMap(reports);
  const target = slug.toLowerCase();
  for (const r of reports) if (map.get(r.id) === target) return r;
  return undefined;
}

export function reportSlugFor<T extends SluggableReport>(reports: T[], id: string): string {
  return reportSlugMap(reports).get(id) ?? id;
}
